import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Building2, Loader2 } from "lucide-react";
import api from "../../api/api";
import { useAuth } from "../../hooks/useAuth";
import { Alert, PageHeader, Panel, Pill, apiError, useOrgStyles, ORG_TYPE_LABEL } from "../../components/org/orgUi";
import { ORG_PAGES, orgPagePath } from "./orgPortalContext";

/**
 * Shell for the organization portal (org admins, licensed org coaches, and
 * BizBrand admins arriving from Admin -> Organizations with ?orgId=). Loads the
 * org once and renders the current page (/org, /org/programs, ...) via <Outlet>.
 */
export default function OrgLayout() {
    const s = useOrgStyles();
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [context, setContext] = useState(null);
    const [org, setOrg] = useState(null);
    const [viewer, setViewer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const requestedOrgId = Number(searchParams.get("orgId")) || null;
    const accessibleOrgs = useMemo(
        () => (context?.organizations || []).filter((o) => o.IsOrgAdmin || o.HasCoachLicense),
        [context]
    );
    const orgId = requestedOrgId || accessibleOrgs[0]?.OrgID || null;

    useEffect(() => {
        api.get("/org/context")
            .then((res) => setContext(res.data))
            .catch((err) => setError(apiError(err, "Couldn't load your organizations.")))
            .finally(() => { if (!orgId) setLoading(false); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadOrg = useCallback(async () => {
        if (!orgId) return;
        try {
            const res = await api.get(`/org/${orgId}`);
            setOrg(res.data.organization);
            setViewer(res.data.viewer);
            setError("");
        } catch (err) {
            setOrg(null);
            setError(apiError(err, "Couldn't load this organization."));
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    useEffect(() => { setLoading(true); loadOrg(); }, [loadOrg]);

    // Old links used /org?tab=<page>; send them to the page's own route.
    const legacyTab = searchParams.get("tab");
    if (legacyTab) {
        const { tab, orgId: legacyOrgId, ...rest } = Object.fromEntries(searchParams);
        return <Navigate to={orgPagePath(tab, { orgId: legacyOrgId, ...rest })} replace />;
    }

    if (loading && !org) {
        return (
            <div className="flex min-h-[50vh] items-center justify-center">
                <Loader2 className={`animate-spin ${s.muted}`} size={28} />
            </div>
        );
    }

    if (!orgId || !org) {
        return (
            <div className="mx-auto max-w-3xl px-4 py-10">
                <PageHeader title="Organization" />
                <Alert>{error}</Alert>
                <Panel>
                    <div className="py-6 text-center">
                        <Building2 className={`mx-auto mb-3 ${s.muted}`} size={32} />
                        <p className={s.body}>
                            {context?.isPlatformAdmin
                                ? <>Open an organization from <Link className={s.btnLink} to="/admin/organizations">Admin → Organizations</Link>.</>
                                : (context?.organizations || []).length
                                    ? "Your organization hasn't given you a coach license for this month yet. Ask your organization admin."
                                    : "You aren't part of an organization yet."}
                        </p>
                    </div>
                </Panel>
            </div>
        );
    }

    const isOrgAdmin = !!viewer?.isOrgAdmin;
    const isPlatformAdmin = !!viewer?.isPlatformAdmin;
    const pages = ORG_PAGES.filter((p) => isOrgAdmin || !p.adminOnly);
    const segment = location.pathname.replace(/^\/org\/?/, "").split("/")[0];
    const current = ORG_PAGES.find((p) => p.path === segment);
    if (!current) return <Navigate to={orgPagePath("overview", { orgId: requestedOrgId })} replace />;
    if (current.adminOnly && !isOrgAdmin) return <Navigate to={orgPagePath("overview", { orgId: requestedOrgId })} replace />;

    const goTo = (key, extra = {}) => navigate(orgPagePath(key, { orgId: requestedOrgId, ...extra }));
    const switchOrg = (id) => navigate(orgPagePath(current.key, { orgId: id }));
    // Org-admin-only logins reach each page from the sidebar. Coaches and
    // BizBrand admins don't have those sidebar links, so they get page links here.
    const showPageLinks = !(user?.isOrgAdmin && !user?.isCoach && !user?.isAdmin);

    return (
        <div className="mx-auto max-w-6xl px-0 md:px-4 py-2 md:py-6">
            <PageHeader
                title={current.key === "overview" ? org.OrgName : current.label}
                subtitle={
                    <span className="inline-flex flex-wrap items-center gap-2">
                        {current.key !== "overview" && <span className={s.body}>{org.OrgName}</span>}
                        <Pill tone="primary">{ORG_TYPE_LABEL[org.OrgType] || org.OrgType}</Pill>
                        {org.Status !== "active" && <Pill tone="warning">{org.Status}</Pill>}
                        {isPlatformAdmin && <Pill tone="neutral">Viewing as Build My Biz Brand admin</Pill>}
                        {!isOrgAdmin && <Pill tone="neutral">Coach view</Pill>}
                    </span>
                }
                actions={accessibleOrgs.length > 1 && (
                    <select
                        aria-label="Switch organization"
                        value={orgId}
                        onChange={(e) => switchOrg(e.target.value)}
                        className={`${s.input} w-auto`}
                    >
                        {accessibleOrgs.map((o) => <option key={o.OrgID} value={o.OrgID}>{o.OrgName}</option>)}
                    </select>
                )}
            />
            {showPageLinks && (
                <nav aria-label="Organization pages" className="mb-6 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold">
                    {pages.map((p) => (
                        <NavLink
                            key={p.key}
                            to={orgPagePath(p.key, { orgId: requestedOrgId })}
                            end={p.key === "overview"}
                            className={({ isActive }) => isActive ? (s.dark ? "text-white" : "text-primary-700") : s.muted}
                        >
                            {p.label}
                        </NavLink>
                    ))}
                </nav>
            )}
            <Alert onClose={() => setError("")}>{error}</Alert>
            <Outlet context={{ orgId, org, viewer, isOrgAdmin, isPlatformAdmin, reloadOrg: loadOrg, goTo }} />
        </div>
    );
}
